import { createTheme, type CSSVariablesResolver, type MantineColorsTuple } from '@mantine/core'

/**
 * The single Mantine theme — docs/spec/07 §1.
 *
 * Every token lives here; components use theme values, never hard-coded colors
 * or spacings. This is a dense, keyboard-first operations tool for staff doing
 * hundreds of repetitions a day (00 §4), not a marketing site — so the defaults
 * below lean compact and low-chrome on purpose.
 */

/** Pigeonly teal, continuous with the legacy tool's accent (07 §5). */
const teal: MantineColorsTuple = [
  '#e0fbfa',
  '#ccf2f1',
  '#9ce3e0',
  '#68d3cf',
  '#42c6c1',
  '#2abeb8',
  '#14bab3',
  '#00a39d',
  '#00918b',
  '#007d78',
]

export const theme = createTheme({
  primaryColor: 'pigeonlyTeal',
  colors: { pigeonlyTeal: teal },

  /**
   * Let Mantine choose the label color on filled surfaces from the background's
   * luminance, instead of always using white.
   *
   * Pigeonly teal is a **light** accent (`teal[6]` `#14bab3`, luminance 0.385),
   * so Mantine's default white label gave the primary button **2.41:1** — under
   * half of the 4.5 AA needs, on the main call-to-action of every screen
   * (found by the Lighthouse gate, 2026-07-29). With `autoContrast` the label
   * goes dark and the same button measures 8.70:1.
   *
   * Chosen over darkening the palette because it **keeps the brand color
   * exactly** — 07 §5 requires continuity with the legacy tool's accent, and
   * that constrains the background, not the label.
   */
  autoContrast: true,

  // A UI stack, not a display face: this is read all day at small sizes.
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  // Parcel codes and ids are compared character-by-character, so they get a
  // monospace face (07 §1).
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',

  headings: { fontWeight: '600' },
  defaultRadius: 'sm',

  components: {
    // Compact density across the data-entry surfaces (07 §1).
    Table: { defaultProps: { verticalSpacing: 'xs', horizontalSpacing: 'sm', fontSize: 'sm' } },
    TextInput: { defaultProps: { size: 'sm' } },
    Select: { defaultProps: { size: 'sm' } },
    MultiSelect: { defaultProps: { size: 'sm' } },
    Checkbox: { defaultProps: { size: 'sm' } },
    Button: { defaultProps: { size: 'sm' } },
  },

  other: {
    /**
     * Tabular numerals for queue dates and codes (07 §1): columns of figures
     * must line up vertically to be scannable at a glance.
     */
    tabularNumbers: 'font-variant-numeric: tabular-nums;',
  },
})

/**
 * Surface colors Mantine composes `dimmed` against — its `--mantine-color-body`
 * in each scheme. Named here so the contrast assertions in `theme.test.ts` test
 * the real pairing rather than a hardcoded guess.
 */
export const BODY_LIGHT = '#ffffff'
export const BODY_DARK = '#242424' // --mantine-color-dark-7

/**
 * Accessible replacements for Mantine's `dimmed`, which **fails WCAG 2.2 AA in
 * both schemes** (measured 2026-07-29 against @mantine/core 9.5):
 *
 * | scheme | Mantine default        | on body   | ratio     |
 * |--------|------------------------|-----------|-----------|
 * | light  | `gray-6`  `#868e96`    | `#ffffff` | **3.32**  |
 * | dark   | `dark-2`  `#828282`    | `#242424` | **4.03**  |
 *
 * Both are under the 4.5 AA needs for normal text, and `dimmed` is not a rare
 * decoration — it carries timestamps, hint text and secondary labels across the
 * queue and the workspace. Rule 8 makes AA non-negotiable, so the token is
 * overridden rather than the requirement waived.
 *
 * The replacements keep a visible step below body text (`dark-0` `#c9c9c9` at
 * 9.37 in dark) so "dimmed" still reads as dimmed — the point is a legible
 * hierarchy, not a flat one.
 */
export const DIMMED_LIGHT = '#69727a' // 4.90 on #ffffff
export const DIMMED_DARK = '#9e9e9e' // 5.79 on #242424

/**
 * Applied by `MantineProvider` (01 §2 providers). A CSS-variable override is the
 * right seam: `dimmed` is consumed as `var(--mantine-color-dimmed)` by Mantine's
 * own components as well as ours, so patching the variable fixes every use at
 * once — including the ones inside Mantine we do not author.
 */
/**
 * Same story, bigger blast radius: **every `variant="light"` badge hue we use
 * fails AA in the light scheme.** Measured in real Chrome 2026-07-29 by
 * `e2e/badge-contrast.spec.ts`, after both the axe suite (no computed styles in
 * jsdom) and Lighthouse (audits `/login`, which has no badges) passed all of
 * them:
 *
 * | hue    | Mantine text | on light-variant bg | ratio    | replacement        |
 * |--------|--------------|---------------------|----------|--------------------|
 * | teal   | `#087f5b`    | `#c3fae8`           | **4.33** | `#076c4d` → 5.57   |
 * | blue   | `#1971c2`    | `#d0ebff`           | **4.07** | `#155a99` → 5.76   |
 * | orange | `#d9480f`    | `#ffe8cc`           | **3.62** | `#a8380c` → 5.46   |
 * | yellow | `#e67700`    | `#fff3bf`           | **2.69** | `#7a4100` → 7.29   |
 *
 * These are not decorations: they are the queue's open / in-progress / awaiting-QA
 * totals (04 §3) and the live-connection badge (04 §3b).
 *
 * Yellow is the interesting one — a hue that light leaves almost no luminance
 * range above its own background, so the replacement reads as a dark amber
 * rather than a yellow. That is the honest trade: the badge states
 * "Reconnecting…" in words (07 §4 forbids colour-only status anyway), so
 * legibility wins over hue fidelity.
 *
 * Each replacement is aimed at ~5.5 rather than a bare 4.5 so a future Mantine
 * palette tweak does not silently drop back under. **Only the light scheme needs
 * this** — dark measures 7:1 or better throughout and is left alone.
 *
 * Token overrides, not per-component `c=` props: every current *and future* light
 * badge inherits the fix instead of re-discovering the bug.
 */
export const LIGHT_VARIANT_TEXT: Readonly<Record<string, string>> = {
  teal: '#076c4d', // 5.57 on #c3fae8
  blue: '#155a99', // 5.76 on #d0ebff
  orange: '#a8380c', // 5.46 on #ffe8cc
  yellow: '#7a4100', // 7.29 on #fff3bf
}

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--mantine-color-dimmed': DIMMED_LIGHT,
    ...Object.fromEntries(
      Object.entries(LIGHT_VARIANT_TEXT).map(([hue, value]) => [
        `--mantine-color-${hue}-light-color`,
        value,
      ]),
    ),
  },
  dark: { '--mantine-color-dimmed': DIMMED_DARK },
})
